import { useMemo, useCallback } from 'react';
import { deepCombine, preparePatchPayload, prepareSecretPatchPayloadData, upsertRestrictedFields } from 'utils/utils';
import {
  Application,
  BankingApplicationDocument,
  DO_APPLICATION_TYPE,
  dotNotationToObject,
  formApplicationSections,
  SectionCompletion,
} from 'client-experience-toolbox';
import { AgreementsMetadata, DigitalOnboardingService } from 'services/digitalOnboardingService';
import { batch, useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/configureStore';
import { ApplicationDocument, DeviceInfo, Location, Company } from 'client-experience-toolbox';
import { FileService } from 'services/fileService';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useApplication } from 'hooks/useApplication';

export const shouldSkipSection = (
  application: Partial<Application> | Partial<BankingApplicationDocument>,
  sectionName: string,
) => {
  if (application.type === DO_APPLICATION_TYPE.BANKING) {
    const isSoleProprietor = application?.applicationData?.company?.ownershipType === 'soleProprietor';
    const sectionsToSkipForSoleProp = ['businessOwnership', 'controlProng'];
    return isSoleProprietor && sectionsToSkipForSoleProp.includes(sectionName);
  }
  return false;
};

export const getNextBankingSection = (application: Partial<Application>, currentSection: number) => {
  let nextSection = currentSection + 1;
  const sections = formApplicationSections(DO_APPLICATION_TYPE.BANKING);
  while (nextSection < sections.length && shouldSkipSection(application, sections[nextSection - 1])) {
    nextSection++;
  }
  return nextSection;
};

export const getPreviousBankingSection = (application: Partial<Application>, currentSection: number) => {
  let previousSection = currentSection - 1;
  const sections = formApplicationSections(DO_APPLICATION_TYPE.BANKING);
  while (previousSection > 0 && shouldSkipSection(application, sections[previousSection - 1])) {
    previousSection--;
  }
  return previousSection;
};

export const useFormNetworkCalls = (type: DO_APPLICATION_TYPE) => {
  const dispatch = useDispatch();
  const { application, section } = useApplication(type);
  const sectionHasChanged = useSelector((state: RootState) => state.applicationReducer.sectionHasChanged);
  const applicationData = application?.applicationData;
  const applicationId = application?._id;
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const digitalOnboardingService = useMemo(() => {
    return new DigitalOnboardingService();
  }, []);
  const fileService = useMemo(() => {
    return new FileService();
  }, []);
  const applicationSections = formApplicationSections(type);
  const sectionName = applicationSections[section - 1] as keyof ApplicationDocument['sectionsCompletion'];

  const patchApplication = async (field: string, value?: unknown, applicantKey?: string) => {
    const isSecretField = upsertRestrictedFields.some((restrictedField) => field.includes(restrictedField));
    const patchPayload = dotNotationToObject(field, value);
    if (!digitalOnboardingService || !applicationId) {
      console.error('Unable to patch application: missing request function or application ID');
      return;
    }

    try {
      if (isSecretField) {
        const secretField = field.split('.').pop();
        const secretPayload = {
          [secretField!]: value,
          key: applicantKey,
        };
        await digitalOnboardingService.patchApplicationSecret(applicationId, secretPayload);
      } else {
        await digitalOnboardingService.patchApplication(applicationId, {
          ...patchPayload,
          sectionsCompletion: {
            ...application.sectionsCompletion,
            [sectionName]: { completedAt: null },
          },
        });
      }
    } catch (error) {
      console.error('Error patching application:', error);
    }
  };

  const patchNeuroData = async (appId: string | undefined, neuroUserId: string, neuroSiteId: string) => {
    if (!digitalOnboardingService || !appId || !neuroUserId || !neuroSiteId) {
      console.error('Digital onboarding service, application ID, or neuro data is not available.');
      return;
    }

    try {
      await digitalOnboardingService.patchApplication(appId, { neuroSiteId, neuroUserId });
    } catch (e) {
      console.error('Failed to patch neuro data for application:', e);
    }
  };

  const patchFullApplication = async (
    setIsPatching: (isPatching: boolean) => void,
    applicationDataOverride?: Partial<Application>['applicationData'],
    sectionCompletionOverride?: Partial<Application>['sectionsCompletion'],
  ) => {
    const dataToUse = applicationDataOverride ?? applicationData;
    if (!digitalOnboardingService || !applicationId || !dataToUse) {
      console.error('Digital onboarding service or application ID is not available.');
      return;
    }
    setIsPatching(true);
    dispatch({ type: 'SET_PATCH_FULL_APPLICATION_ERROR', patchFullApplicationError: false });

    const applicationPatchData = { applicationData: dataToUse };
    const isSectionComplete = application.sectionsCompletion?.[sectionName]?.completedAt;

    if (sectionHasChanged || !isSectionComplete) {
      const isSolePropSkippingSections =
        DO_APPLICATION_TYPE.BANKING === type &&
        application.applicationData.company['ownershipType'] === 'soleProprietor';

      if (isSolePropSkippingSections) {
        const bankingApplicationData =
          applicationPatchData.applicationData as BankingApplicationDocument['applicationData'];
        bankingApplicationData.company['hasMajorOwner'] = null;
        const primaryApplicant = bankingApplicationData.applicants?.[0];
        bankingApplicationData.applicants = primaryApplicant
          ? [{ ...primaryApplicant, ownershipPercentage: null }]
          : [];
      }
      if (sectionCompletionOverride) {
        applicationPatchData['sectionsCompletion'] = {
          ...application.sectionsCompletion,
          ...sectionCompletionOverride,
        };
      } else {
        applicationPatchData['sectionsCompletion'] = {
          ...application.sectionsCompletion,
          [sectionName]: { completedAt: Date.now().valueOf() },
          // For sole proprietors, mark skipped sections so navigation works correctly
          ...(isSolePropSkippingSections && { businessOwnership: { completedAt: null } }),
          ...(isSolePropSkippingSections && { controlProng: { completedAt: null } }),
        };
      }
    }

    const patchPayload = preparePatchPayload(applicationPatchData);

    const secretPatchPayloadData = prepareSecretPatchPayloadData(applicationPatchData.applicationData);

    try {
      await digitalOnboardingService.patchApplication(applicationId, patchPayload);

      if (secretPatchPayloadData.companyIrsTaxIdOrEin) {
        await digitalOnboardingService.patchApplicationSecret(application._id, {
          irsTaxIdOrEin: secretPatchPayloadData.companyIrsTaxIdOrEin,
        });
      }

      for (const applicant of secretPatchPayloadData.applicants) {
        await digitalOnboardingService.patchApplicationSecret(application._id, {
          key: applicant.key,
          ssnOrItin: applicant.ssnOrItin,
        });
      }

      switch (type) {
        case DO_APPLICATION_TYPE.FACTORING:
          dispatch({ type: 'SET_FACTORING_SECTION', factoringSection: section + 1 });
          dispatch({
            type: 'SET_FACTORING_APPLICATION',
            factoringApplication: deepCombine(
              { ...application, sectionsCompletion: { ...application.sectionsCompletion } },
              patchPayload,
            ),
            sectionHasChanged: false,
          });
          break;
        case DO_APPLICATION_TYPE.BANKING:
          dispatch({ type: 'SET_BANKING_SECTION', bankingSection: getNextBankingSection(application, section) });
          dispatch({
            type: 'SET_BANKING_APPLICATION',
            bankingApplication: deepCombine(
              { ...application, applicationData: dataToUse, sectionsCompletion: { ...application.sectionsCompletion } },
              patchPayload,
            ),
            sectionHasChanged: false,
          });
          break;
        default:
          break;
      }
    } catch (e) {
      console.error('Failed to patch application:', e);
      dispatch({ type: 'SET_PATCH_FULL_APPLICATION_ERROR', patchFullApplicationError: true });
    } finally {
      setIsPatching(false);
    }
  };

  const getCompanyByDOT = async (dot_number: string, type?: string) =>
    await digitalOnboardingService.getCompanyByDOT(dot_number, type);

  const deleteFileFromS3 = async (fileName: string) => await fileService.deleteFileFromS3(fileName);

  const getPresignedUrl = async (sourceType: string, documentType: string, filePath: string) =>
    await digitalOnboardingService.getPresignedUrl(sourceType, documentType, filePath);

  const getAgreements = useCallback(
    async (
      applicationType: string,
      documentType: string,
    ): Promise<{ metadata: AgreementsMetadata; pdfData: Uint8Array }> => {
      const { presignedUrl, metadata } = await digitalOnboardingService.getAgreements({
        type: applicationType,
        documentType,
        language: currentLanguage || 'en',
      });
      const response = await fetch(presignedUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch agreement PDF content');
      }
      const pdfBuffer = await response.arrayBuffer();

      return { metadata, pdfData: new Uint8Array(pdfBuffer) };
    },
    [digitalOnboardingService, currentLanguage],
  );

  const updateAgreementMetadata = async (documentType: string, metadata?: AgreementsMetadata) => {
    if (!applicationId || !metadata) {
      return;
    }

    const agreementData = {
      ...metadata,
      timestamp: Date.now(),
    };

    const patchPayload = preparePatchPayload({
      applicationData: {
        applicants: [{ [documentType]: agreementData }],
      },
    });

    await digitalOnboardingService.patchApplication(applicationId, patchPayload as Application);

    switch (type) {
      case DO_APPLICATION_TYPE.FACTORING:
        dispatch({
          type: 'SET_FACTORING_APPLICATION',
          factoringApplication: deepCombine({ ...application }, patchPayload),
        });
        break;
      case DO_APPLICATION_TYPE.BANKING:
        dispatch({
          type: 'SET_BANKING_APPLICATION',
          bankingApplication: deepCombine({ ...application }, patchPayload),
        });
        break;
      default:
        break;
    }
  };

  const fetchApplication = async (applicationId: string) => {
    try {
      const applicationData = await digitalOnboardingService.getApplicationById(applicationId);
      return applicationData;
    } catch (error) {
      console.error('Error fetching application data:', error);
    }
  };

  const getLegalText = useCallback(async () => {
    if (!digitalOnboardingService || !application.type) {
      return;
    }

    try {
      const legalText = await digitalOnboardingService.getLegalText(application.type, currentLanguage);
      return legalText;
    } catch (error) {
      console.error('Error fetching legal text:', error);
    }
  }, [digitalOnboardingService, application, currentLanguage]);

  const uploadDocumentToS3 = async (file: File, presignedUrl: string) => {
    const headers = {
      'Content-Type': file.type,
    };
    const options = {
      headers: headers,
    };
    await axios.put(presignedUrl, file, options);
  };

  const patchApplicationPullData = async (applicationId: string, body) => {
    if (!digitalOnboardingService) {
      console.error('Digital onboarding service is not available.');
      return;
    }
    try {
      await digitalOnboardingService.patchApplicationPullData(applicationId, body);
    } catch (error) {
      console.error('Error patching application pull data:', error);
    }
  };

  const submitApplication = async (body: { location: Location; deviceInfo: DeviceInfo }) => {
    if (!digitalOnboardingService || !applicationId) {
      console.error('Digital onboarding service or application ID is not available.');
      return;
    }
    try {
      await digitalOnboardingService.submitApplication(applicationId, body);
    } catch (error) {
      console.error('Error submitting application:', error);
      throw error;
    }
  };

  const submitBackgroundCheck = async (applicationId: string, type: 'kyc' | 'kyb') => {
    if (!digitalOnboardingService) {
      console.error('Digital onboarding service is not available.');
      return;
    }
    try {
      const response = await digitalOnboardingService.submitBackgroundCheck(applicationId, type);
      return response;
    } catch (error) {
      console.error('Error submitting background check:', error);
      throw error;
    }
  };

  const applyPrefillData = async (prefillData: Record<string, Partial<Company>>) => {
    if (!digitalOnboardingService || !applicationId) {
      console.error('Digital onboarding service or application ID is not available.');
      throw new Error('Service not available');
    }

    try {
      const patchPayload = preparePatchPayload({
        applicationData: {
          company: { ...application?.applicationData?.company, ...prefillData?.company },
        },
      });

      await digitalOnboardingService.patchApplication(applicationId, patchPayload);

      batch(() => {
        dispatch({
          type: 'SET_FACTORING_APPLICATION',
          factoringApplication: deepCombine({ ...application }, patchPayload),
          sectionHasChanged: false,
        });
      });

      return true;
    } catch (error) {
      console.error('Error applying prefill data:', error);
      throw error;
    }
  };

  const setSectionComplete = async (sectionsCompletion: { [key: string]: SectionCompletion }) => {
    if (!applicationId) {
      return;
    }

    await digitalOnboardingService.patchApplication(applicationId, { sectionsCompletion: sectionsCompletion });
    dispatch({
      type: 'SET_BANKING_APPLICATION',
      bankingApplication: {
        ...application,
        sectionsCompletion: {
          ...application?.sectionsCompletion,
          ...sectionsCompletion,
        },
      },
    });
  };

  return {
    patchApplication,
    patchNeuroData,
    patchFullApplication,
    getCompanyByDOT,
    getLegalText,
    fetchApplication,
    submitApplication,
    submitBackgroundCheck,
    patchApplicationPullData,
    deleteFileFromS3,
    getPresignedUrl,
    getAgreements,
    updateAgreementMetadata,
    uploadDocumentToS3,
    applyPrefillData,
    setSectionComplete,
  };
};
